// Streak.js
// Streak Mode gameplay. Built on the Draft.js template — shares the header,
// progress row, PlayerCard list, and mechanic buttons.
//
// Loop (mirrors StreakLogic.swift / StreakGameView.swift):
//   • Show 5 random players with hidden ratings.
//   • Pick the highest-rated → streak +1, fresh 5, reset the 10s timer.
//   • Wrong pick OR 10s timeout → a strike. 3 strikes ends the game.
//   • Final score = streak length.
//   • One Rating Reveal (shows 3 of 5 ratings) and one Player Refresh per game.
//   • Players never repeat within a game.

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import wordmark from "../assets/Rosterable.png";
import footballIcon from "../assets/football.svg";
import baseballIcon from "../assets/baseball-fill.svg";
import basketballIcon from "../assets/dribbble-logo-fill.svg";
import hockeyIcon from "../assets/hockey-fill.svg";
import soccerIcon from "../assets/soccer-ball-fill.svg";
import binocularsIcon from "../assets/binoculars-fill.svg";
import repeatIcon from "../assets/repeat-fill.svg";
import lightningIcon from "../assets/lightning-fill.svg";
import PlayerCard from "../components/PlayerCard";
import { useAuth } from "../managers/AuthContext";
import { fetchPlayersForSport, getRandomPlayers, recordStreakResult } from "../managers/streakService";
import { submitStreakScore } from "../managers/leaderboardService";
import { loadTeamLogos } from "../managers/teamLogoService";

const SPORT_ICONS = {
  football: footballIcon,
  baseball: baseballIcon,
  basketball: basketballIcon,
  hockey: hockeyIcon,
  soccer: soccerIcon,
};

const ROUND_SECONDS = 10;

export default function Streak() {
  const navigate = useNavigate();
  const location = useLocation();
  const sport = location.state?.sport;

  useEffect(() => {
    if (!sport) navigate("/");
  }, [sport, navigate]);

  if (!sport) return null;
  return <StreakGame sport={sport} navigate={navigate} />;
}

function StreakGame({ sport, navigate }) {
  const { user, freePlay } = useAuth();
  const ownerId = user?.uid || "guest";

  // ─── Game state ────────────────────────────────────────────────────────────
  const [players, setPlayers] = useState([]);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [strikeCount, setStrikeCount] = useState(0);
  const [revealedIndices, setRevealedIndices] = useState(new Set());
  const [usedReveal, setUsedReveal] = useState(false);
  const [usedRefresh, setUsedRefresh] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [feedback, setFeedback] = useState({ show: false, correct: new Set(), incorrect: null });
  const [isProcessing, setIsProcessing] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(ROUND_SECONDS);
  const [timerKey, setTimerKey] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [, bumpLogos] = useState(0);

  // Refs the timer / async flows read so they never see stale closures.
  const allPlayers = useRef([]);
  const usedNames = useRef(new Set());
  const isProcessingRef = useRef(false);
  const gameOverRef = useRef(false);
  const strikeRef = useRef(0);
  const streakRef = useRef(0);

  useEffect(() => { isProcessingRef.current = isProcessing; }, [isProcessing]);

  // ─── Load logos + player pool, then deal the first 5 ─────────────────────────
  useEffect(() => {
    loadTeamLogos().then(() => bumpLogos(n => n + 1)).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setIsLoading(true);
      setLoadError(null);
      try {
        const pool = await fetchPlayersForSport(sport.id);
        if (cancelled) return;
        allPlayers.current = pool;
        const first = getRandomPlayers(pool, 5, usedNames.current);
        first.forEach(p => usedNames.current.add(p.name));
        setPlayers(first);
        setIsLoading(false);
        setTimerKey(k => k + 1);   // start the timer
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to load players:", err);
        setLoadError("Couldn't load players. Check your connection.");
        setIsLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [sport.id]);

  // ─── Helpers ─────────────────────────────────────────────────────────────────
  const draw = useCallback(() => {
    const next = getRandomPlayers(allPlayers.current, 5, usedNames.current);
    next.forEach(p => usedNames.current.add(p.name));
    return next;
  }, []);

  // Start a fresh round: new cards already set by caller, just reset per-round UI.
  const beginNextRound = useCallback((nextPlayers) => {
    setPlayers(nextPlayers);
    setRevealedIndices(new Set());
    setSelectedIndex(null);
    setFeedback({ show: false, correct: new Set(), incorrect: null });
    setIsProcessing(false);
    isProcessingRef.current = false;
    setTimerKey(k => k + 1);   // restart the 10s timer
  }, []);

  const endGame = useCallback((finalScore) => {
    gameOverRef.current = true;
    // Free Play leaves no footprint: skip both the local games-played counter
    // and the leaderboard write. Everything else (the complete screen) is normal.
    if (!freePlay) {
      recordStreakResult(ownerId, sport.id, finalScore);   // local: games played
      // Best-effort leaderboard write (internally a no-op for guests / streak < 1).
      submitStreakScore({ uid: user?.uid, username: user?.username, sport, streakCount: finalScore })
        .catch(err => console.warn("Streak leaderboard submit failed:", err));
    }
    navigate("/streak-complete", { replace: true, state: { sport, finalScore } });
  }, [navigate, ownerId, sport, user, freePlay]);

  const applyStrike = useCallback(() => {
    const newStrikes = strikeRef.current + 1;
    strikeRef.current = newStrikes;
    setStrikeCount(newStrikes);
    if (newStrikes >= 3) {
      endGame(streakRef.current);
    } else {
      beginNextRound(draw());
    }
  }, [beginNextRound, draw, endGame]);

  const advance = useCallback(() => {
    const newStreak = streakRef.current + 1;
    streakRef.current = newStreak;
    setCurrentStreak(newStreak);
    beginNextRound(draw());
  }, [beginNextRound, draw]);

  // ─── Pick a player ───────────────────────────────────────────────────────────
  function selectPlayer(index) {
    if (isProcessingRef.current || gameOverRef.current || isLoading) return;
    const maxRating = Math.max(...players.map(p => p.overallRating));
    const correct = new Set(players.map((p, i) => (p.overallRating === maxRating ? i : -1)).filter(i => i >= 0));
    const isCorrect = players[index].overallRating === maxRating;

    setSelectedIndex(index);
    setIsProcessing(true);
    isProcessingRef.current = true;

    if (isCorrect) {
      // Flash all cards tied for the highest rating, then advance.
      setFeedback({ show: true, correct, incorrect: null });
      setTimeout(() => advance(), 500);
    } else {
      // Flash the wrong pick red (and the correct ones green), then strike.
      setFeedback({ show: true, correct, incorrect: index });
      setTimeout(() => applyStrike(), 650);
    }
  }

  // ─── Timeout = a strike (no card was picked) ─────────────────────────────────
  const handleTimeout = useCallback(() => {
    if (isProcessingRef.current || gameOverRef.current) return;
    setIsProcessing(true);
    isProcessingRef.current = true;
    setTimeout(() => applyStrike(), 350);
  }, [applyStrike]);

  // Keep the latest handleTimeout reachable from the interval closure.
  const handleTimeoutRef = useRef(handleTimeout);
  handleTimeoutRef.current = handleTimeout;

  // ─── Per-round 10s countdown ─────────────────────────────────────────────────
  // Re-created whenever timerKey bumps (new round or power-up). Paused while a
  // selection is being processed via isProcessingRef.
  useEffect(() => {
    if (isLoading || gameOverRef.current || timerKey === 0) return;
    let t = ROUND_SECONDS;
    setTimeRemaining(t);
    const id = setInterval(() => {
      if (isProcessingRef.current) return;
      t = Math.max(0, t - 0.1);
      setTimeRemaining(t);
      if (t <= 0) {
        clearInterval(id);
        handleTimeoutRef.current();
      }
    }, 100);
    return () => clearInterval(id);
  }, [timerKey, isLoading]);

  // ─── Power-ups ───────────────────────────────────────────────────────────────
  function handleReveal() {
    if (usedReveal || isProcessing || isLoading) return;
    const idx = [...players.keys()].sort(() => Math.random() - 0.5).slice(0, 3);
    setRevealedIndices(new Set(idx));
    setUsedReveal(true);
    setTimerKey(k => k + 1);   // resets the timer, keeps players + reveal
  }

  function handleRefresh() {
    if (usedRefresh || isProcessing || isLoading) return;
    setUsedRefresh(true);
    const next = draw();
    setRevealedIndices(new Set());
    setPlayers(next);
    setSelectedIndex(null);
    setFeedback({ show: false, correct: new Set(), incorrect: null });
    setTimerKey(k => k + 1);
  }

  // ─── Derived ─────────────────────────────────────────────────────────────────
  const timerPct = Math.max(0, Math.min(1, timeRemaining / ROUND_SECONDS));
  const timerLow = timerPct <= 0.3;
  const timerSeconds = Math.ceil(timeRemaining);

  return (
    <div style={styles.container}>
      {/* ── Header ── */}
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={() => navigate("/")}>‹</button>
        <img src={wordmark} alt="Rosterable" style={styles.wordmark} />
        <div style={styles.counterBadge}>
          <img src={lightningIcon} alt="" style={styles.counterIcon} />
          <span style={styles.counterText}>{currentStreak}</span>
        </div>
      </div>

      {/* ── Progress (streak + timer) ── */}
      <div style={styles.progressSection}>
        <div style={styles.progressRow}>
          <div style={{ ...styles.sportCircle, background: sport.hex }}>
            <img src={SPORT_ICONS[sport.id]} alt={sport.name} style={styles.sportCircleIcon} />
          </div>
          <div style={styles.streakLabel}>
            <span style={styles.streakLabelText}>CURRENT STREAK</span>
            <span style={{ ...styles.streakValue, color: sport.hex }}>{currentStreak}</span>
          </div>
          <span style={{ ...styles.timerText, color: timerLow ? "#ff5c5c" : "rgba(255,255,255,0.85)" }}>
            :{timerSeconds < 10 ? `0${timerSeconds}` : timerSeconds}
          </span>
        </div>
        <div style={styles.progressBarTrack}>
          <div style={{
            ...styles.progressBarFill,
            width: `${timerPct * 100}%`,
            background: timerLow
              ? "linear-gradient(to right, #ff5c5c, #ff5c5ccc)"
              : `linear-gradient(to right, ${sport.hex}, ${sport.hex}cc)`,
            boxShadow: `0 0 6px ${timerLow ? "#ff5c5c66" : `${sport.hex}66`}`,
            transition: "width 0.12s linear",
          }} />
        </div>
      </div>

      {/* ── Player cards ── */}
      <div style={styles.cardsSection}>
        {isLoading ? (
          <div style={styles.loadingState}><span style={styles.loadingText}>Loading players...</span></div>
        ) : loadError ? (
          <div style={styles.loadingState}>
            <span style={styles.loadingText}>{loadError}</span>
            <button style={styles.retryBtn} onClick={() => window.location.reload()}>RETRY</button>
          </div>
        ) : (
          players.map((player, index) => (
            <PlayerCard
              key={`${timerKey}-${index}`}
              player={player}
              sport={sport}
              position={player.position}
              isSelected={selectedIndex === index}
              showRating={revealedIndices.has(index)}
              showCorrectFeedback={feedback.show && feedback.correct.has(index)}
              showIncorrectFeedback={feedback.show && feedback.incorrect === index}
              isDisabled={isProcessing}
              onTap={() => selectPlayer(index)}
            />
          ))
        )}
      </div>

      {/* ── Power-up buttons ── */}
      <div style={styles.mechanicsRow}>
        <MechanicButton label="RATING REVEAL" iconSrc={binocularsIcon} isUsed={usedReveal} sportColor={sport.hex} onClick={handleReveal} />
        <MechanicButton label="PLAYER REFRESH" iconSrc={repeatIcon} isUsed={usedRefresh} sportColor={sport.hex} onClick={handleRefresh} />
      </div>

      {/* ── Strike indicators ── */}
      <div style={{ ...styles.strikeBox, borderColor: `${sport.hex}4d` }}>
        {[0, 1, 2].map(i => {
          const filled = i < strikeCount;
          return (
            <div key={i} style={{
              ...styles.strikeDot,
              background: filled ? "rgba(255,59,48,0.18)" : "rgba(255,255,255,0.05)",
              borderColor: filled ? "rgba(255,59,48,0.55)" : "rgba(255,255,255,0.14)",
            }}>
              {filled && <span style={styles.strikeX}>✕</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Mechanic Button (matches Draft.js) ─────────────────────────────────────────
function MechanicButton({ label, iconSrc, isUsed, sportColor, onClick }) {
  const [isPressed, setIsPressed] = useState(false);
  return (
    <button
      style={{
        ...styles.mechanicBtn,
        opacity: isUsed ? 0.4 : 1,
        background: isUsed ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.08)",
        borderColor: isUsed ? "rgba(255,255,255,0.1)" : `${sportColor}4d`,
        transform: isPressed && !isUsed ? "scale(0.98)" : "scale(1)",
        transition: "all 0.1s ease",
        cursor: isUsed ? "default" : "pointer",
      }}
      onClick={isUsed ? undefined : onClick}
      onMouseDown={() => { if (!isUsed) setIsPressed(true); }}
      onMouseUp={() => setIsPressed(false)}
      onMouseLeave={() => setIsPressed(false)}
    >
      {!isUsed && iconSrc && <img src={iconSrc} alt="" style={styles.mechanicIcon} />}
      <span style={{ ...styles.mechanicLabel, color: isUsed ? "rgba(255,255,255,0.4)" : "#ffffff" }}>
        {isUsed ? "✓ USED" : label}
      </span>
    </button>
  );
}

// ─── Styles ─────────────────────────────────────────────────────────────────────

const styles = {
  container: {
    minHeight: "100vh",
    width: "100%",
    maxWidth: "480px",
    margin: "0 auto",
    padding: "0 16px 32px",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingTop: "52px",
    paddingBottom: "16px",
  },
  backBtn: {
    background: "none",
    border: "none",
    color: "rgba(255,255,255,0.8)",
    fontSize: "28px",
    fontWeight: "300",
    lineHeight: 1,
    padding: "4px 8px 4px 0",
    width: "44px",
    cursor: "pointer",
  },
  wordmark: { height: "21px", width: "auto" },
  counterBadge: {
    width: "44px",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "6px",
  },
  counterIcon: { width: "16px", height: "16px", filter: "brightness(0) invert(1)" },
  counterText: { fontSize: "18px", fontWeight: "800", color: "#fff", fontVariantNumeric: "tabular-nums" },

  progressSection: {
    paddingBottom: "20px",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  progressRow: { display: "flex", alignItems: "center", gap: "12px" },
  sportCircle: {
    width: "44px",
    height: "44px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  sportCircleIcon: { width: "26px", height: "26px", filter: "brightness(0)" },
  streakLabel: { display: "flex", alignItems: "center", gap: "8px", flex: 1 },
  streakLabelText: { fontSize: "17px", fontWeight: "500", color: "rgba(255,255,255,0.7)", letterSpacing: "0.5px" },
  streakValue: { fontSize: "17px", fontWeight: "800", letterSpacing: "1px" },
  timerText: { fontSize: "16px", fontWeight: "700", letterSpacing: "1px", fontVariantNumeric: "tabular-nums" },
  progressBarTrack: {
    height: "8px",
    borderRadius: "4px",
    background: "rgba(255,255,255,0.08)",
    overflow: "hidden",
    marginLeft: "56px",
  },
  progressBarFill: { height: "100%", borderRadius: "4px" },

  cardsSection: { display: "flex", flexDirection: "column", gap: "8px", paddingTop: "8px" },
  loadingState: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "16px",
    padding: "40px 0",
  },
  loadingText: { fontSize: "14px", color: "rgba(255,255,255,0.4)", fontWeight: "500" },
  retryBtn: {
    padding: "10px 24px",
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.2)",
    background: "rgba(255,255,255,0.06)",
    color: "#fff",
    fontWeight: "700",
    cursor: "pointer",
  },

  mechanicsRow: { display: "flex", gap: "12px", paddingTop: "12px", paddingBottom: "8px" },
  mechanicBtn: {
    flex: 1,
    height: "44px",
    borderRadius: "10px",
    border: "1px solid",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  },
  mechanicIcon: { width: "16px", height: "16px", filter: "brightness(0) invert(1)", flexShrink: 0 },
  mechanicLabel: { fontSize: "13px", fontWeight: "600", letterSpacing: "0.5px" },

  strikeBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "14px",
    padding: "10px 28px",
    marginTop: "4px",
    borderRadius: "12px",
    border: "1px solid",
    background: "rgba(255,255,255,0.04)",
  },
  strikeDot: {
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    border: "1.5px solid",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s ease",
  },
  strikeX: { fontSize: "13px", fontWeight: "800", color: "#ff5c5c", lineHeight: 1 },
};
